import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { track } from "@/lib/analytics";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ReferralQuestion } from "@/components/ReferralQuestion";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [payType, setPayType] = useState("order");
  const { clearCart } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayType(data.type || "order");
          setState("paid");
          track("purchase_completed", { type: data.type || "order", session_id: sessionId });
          if ((data.type || "order") === "order") clearCart();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch {
        // keep polling
      }
      if (attempts.current >= 10) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="pt-40 pb-32 min-h-screen flex items-center justify-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center px-6 max-w-lg">
        {state === "checking" && (
          <>
            <Loader2 size={40} className="text-[#C87455] mx-auto mb-6 animate-spin" strokeWidth={1.5} />
            <h1 className="font-serif-display font-light text-4xl text-white tracking-tight">Confirming your payment…</h1>
            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 mt-4">Please don't close this page</p>
          </>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 size={48} className="text-[#C87455] mx-auto mb-6" strokeWidth={1} />
            <h1 className="font-serif-display font-light text-4xl md:text-5xl text-white tracking-tight">
              {payType === "giftcard" ? "Gift card sent." : payType === "setup_fee" ? "Setup fee received." : "Order confirmed."}
            </h1>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              {payType === "giftcard"
                ? "The gift card is on its way to the recipient's inbox right now, complete with their unique code and your message."
                : payType === "setup_fee"
                ? "Thank you — your artwork is now on file and your order moves into production. Future reorders of this design carry no setup fee."
                : "Your piece joins the queue at the workbench. If you added an engraving, we'll email a digital proof before anything is burned. Turnaround is 5–7 business days after proof approval."}
            </p>
            {payType === "order" && <ReferralQuestion sessionId={sessionId} />}
            <Link to="/shop" data-testid="continue-shopping-btn" className="inline-block mt-10 border border-[#A9B0B7]/50 text-[#A9B0B7] hover:bg-[#A9B0B7] hover:text-black transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm">
              Continue Browsing
            </Link>
            <p className="mt-6 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-zinc-600">
              Follow progress anytime on the{" "}
              <Link to="/track" data-testid="success-track-link" className="text-[#C87455] hover:text-[#A65B3E] transition-colors underline underline-offset-4">
                order tracking page
              </Link>
            </p>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-error">
            <AlertTriangle size={44} className="text-zinc-500 mx-auto mb-6" strokeWidth={1} />
            <h1 className="font-serif-display font-light text-4xl text-white tracking-tight">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-zinc-400 mt-4">
              {state === "timeout"
                ? "Your payment is taking a moment to confirm. Check your email for the receipt — your order is safe."
                : "We couldn't verify this payment. If you were charged, contact us and we'll sort it immediately."}
            </p>
            <Link to="/" className="inline-block mt-10 border border-zinc-700 text-zinc-300 hover:bg-white hover:text-black transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm">
              Back to Studio
            </Link>
          </div>
        )}
      </motion.div>
    </main>
  );
}
